import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get, noop } from 'lodash-es';
import { Button } from 'reactstrap';
import { useSelector, connect } from 'react-redux';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { PageModel } from 'client/data/models/page';
import { InventoryEntities } from 'client/data/models/inventory';
import { DealerInfoEntities } from 'client/data/models/dealer-info';
import { CARAMEL_ENTRY_POINT_DATA, CARAMEL_MAKE_AN_OFFER_CTA } from 'site-modules/shared/constants/financing/caramel';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { BUTTON_TYPES } from 'site-modules/shared/components/inventory/contact-dealer/sticky-bar/utils/button-types';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { getVdpCtas, LEAD_FORM_CTAS } from 'site-modules/shared/utils/inventory/get-vdp-ctas';
import { mapLeadFormCTAsToConfig } from 'site-modules/shared/components/inventory/utils/lead-form-ctas';
import { getButtonsConfig } from 'site-modules/shared/components/inventory/contact-dealer/sticky-bar/utils/get-buttons-config';
import { getDataTrackingParent } from 'site-modules/shared/components/inventory/contact-dealer/sticky-bar/utils/get-data-tracking-parent';
import { LeadFormDrawer } from 'site-modules/shared/components/lead-form/lead-form-drawer/lead-form-drawer';
import { WhiteBackButton } from 'site-modules/shared/components/lead-form/lead-form-drawer/close-buttons/white-back-button/white-back-button';
import { DrawerV2 } from 'site-modules/shared/components/drawer/drawer-v2';
import { EmailButton } from 'site-modules/shared/components/inventory/contact-dealer/sticky-bar/email-button/email-button';
import { DeliveryButton } from 'site-modules/shared/components/inventory/contact-dealer/sticky-bar/delivery-button/delivery-button';
import { isPrivateParty } from 'site-modules/shared/utils/dealer-details-utils';
import { ButtonsRenderer } from 'site-modules/shared/components/inventory/contact-dealer/sticky-bar/buttons-renderer/buttons-renderer';
import { ContactSellerButtonWithDrawer } from 'site-modules/shared/components/private-party/contact-seller/contact-seller-button-with-drawer';
import { ContactSeller } from 'site-modules/shared/components/private-party/contact-seller/contact-seller';
import { CaramelCta } from 'site-modules/shared/components/financing/caramel/components/caramel-cta/caramel-cta';

import './sticky-bar.scss';

const StickyBarUI = ({
  vinData,
  location = {},
  hideLeadForm = false,
  ctasOptions = {},
  isVlaPage = false,
  isProdEnv = false,
  dealerInfo = {},
  isCapOneAvailable = false,
  isCapOneIneligible = false,
  isCaramelOptInDealer = false,
  handleCaramelMakeOffer = noop,
  ted6504Recipe = 'ctrl',
  privateListingData = undefined,
  dealer = null,
  ted7485Recipe = 'ctrl',
}) => {
  const [isOpen, toggleLeadFormDrawer] = useToggle(false);
  const [isOpenContactSellerDrawer, toggleContactSellerDrawer] = useToggle(false);

  const pageName = useSelector(state => state.pageContext.page.name);
  const pageNameLegacy = useSelector(
    state => get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name')
  );
  const zipCode = useSelector(state => state.visitor.location.zipCode);
  const enableCaramel = useSelector(state => !!state.featureFlags?.enableCaramel);
  const isForceDefault = useSelector(state => state?.forceDefault);
  const isPrivatePartyListing = isPrivateParty(vinData);

  const dealerFranchiseId = get(vinData, 'dealerInfo.franchiseId', '');
  const dealerRooftopId = get(vinData, 'dealerInfo.rooftopId', '');

  const resultCta = isPrivatePartyListing ? CARAMEL_MAKE_AN_OFFER_CTA : CARAMEL_ENTRY_POINT_DATA.cta;

  const isTed7485Recipe = ['chal-1', 'chal-2', 'chal-3'].includes(ted7485Recipe);

  const phoneButton = useCallback(
    ({ phoneNumber }) => (
      <Button
        size="lg"
        color="outline-blue-50"
        className="px-0_5 py-0_5 text-transform-none w-100 rounded-8 me-0_75"
        href={`tel:${phoneNumber}`}
        data-tracking-id="vin_call_dealer"
        data-dealer-franchise-id={dealerFranchiseId}
        data-dealer-rooftop-id={dealerRooftopId}
        data-tracking-ignore-value
        data-testid="sticky-bar-phone-button"
      >
        <span>Call Dealer</span>
      </Button>
    ),
    [dealerFranchiseId, dealerRooftopId]
  );

  const leadFormButton = useCallback(
    ({ leadFormData }) => (
      <EmailButton
        ariaLabel={`${leadFormData.text}: email dealer`}
        toggleLeadFormDrawer={toggleLeadFormDrawer}
        btnClass="px-0_5 py-0_5 text-transform-none w-100 rounded-8"
        btnColor="blue-50"
        titleClass="fw-medium"
        title={leadFormData.text}
      />
    ),
    [toggleLeadFormDrawer]
  );

  const purchaseButton = useCallback(
    () => (
      <CaramelCta
        dealStatus={dealer?.dealStatus}
        handleCaramelMakeOffer={handleCaramelMakeOffer}
        isPrivateListing={isPrivatePartyListing}
        containerClassName={'mt-0'}
        cta={resultCta}
        domainStyles={{
          height: '20px',
          marginTop: '5px',
        }}
        customStyles={{
          borderRadius: '4px',
          display: 'flex',
          fontWeight: 500,
          justifyContent: 'center',
          alignItems: 'center',
          ...(['chal-2', 'chal-3'].includes(ted7485Recipe) && isPrivatePartyListing
            ? {
                color: '#2070E8',
                background: 'white',
                border: '1px solid #2070E8',
              }
            : { color: 'white', background: '#2070E8' }),
        }}
      />
    ),
    [handleCaramelMakeOffer, isPrivatePartyListing, resultCta, ted7485Recipe, dealer?.dealStatus]
  );

  const contactSellerProps = useMemo(
    () => ({
      sellerLocation: [privateListingData?.sellerCity, privateListingData?.sellerState].filter(Boolean).join(', '),
      vehicleTitle: [privateListingData?.year, privateListingData?.make, privateListingData?.model]
        .filter(Boolean)
        .join(' '),
      vehiclePrice: privateListingData?.internetPrice,
      onSubmitOffer: handleCaramelMakeOffer,
      isOfferLoading: false,
      offerDealStatus: dealer?.dealStatus,
      listingId: privateListingData?.listingId,
    }),
    [privateListingData, handleCaramelMakeOffer, dealer]
  );

  const contactSellerButton = useCallback(
    () => (
      <ContactSellerButtonWithDrawer
        toggleContactSellerDrawer={toggleContactSellerDrawer}
        isOpenContactSellerDrawer={isOpenContactSellerDrawer}
        isPrimary={['chal-2', 'chal-3'].includes(ted7485Recipe)}
      >
        <ContactSeller
          {...contactSellerProps}
          isAskQuestionOnly
          isDrawerEmbedded
          onCloseDrawer={toggleContactSellerDrawer}
        />
      </ContactSellerButtonWithDrawer>
    ),
    [contactSellerProps, isOpenContactSellerDrawer, toggleContactSellerDrawer, ted7485Recipe]
  );

  const prequalifyButton = useCallback(
    () => (
      <Button
        size="lg"
        color="blue-50"
        className="px-0_5 py-0_5 text-transform-none w-100 rounded-8 fw-medium"
        href={`/financing/${!isCapOneAvailable || isCapOneIneligible ? 'prequalify-and-shop' : vinData.vin}/basics`}
        data-tracking-id="financing_cta"
      >
        <span>Get Prequalified</span>
      </Button>
    ),
    [isCapOneAvailable, isCapOneIneligible, vinData.vin]
  );

  const deliveryButton = useCallback(
    () => (
      <DeliveryButton
        vinData={vinData}
        zipCode={zipCode}
        dealerInfo={dealerInfo}
        isProdEnv={isProdEnv}
        ted6504Recipe={ted6504Recipe}
        isForceDefault={isForceDefault}
      />
    ),
    [dealerInfo, isForceDefault, isProdEnv, ted6504Recipe, vinData, zipCode]
  );

  const COMPONENT_MAP = useMemo(
    () => ({
      [BUTTON_TYPES.PHONE]: phoneButton,
      [BUTTON_TYPES.LEAD_FORM]: leadFormButton,
      [BUTTON_TYPES.PURCHASE]: purchaseButton,
      [BUTTON_TYPES.PREQUALIFY]: prequalifyButton,
      [BUTTON_TYPES.DELIVERY]: deliveryButton,
      [BUTTON_TYPES.CONTACT_SELLER]: contactSellerButton,
    }),
    [phoneButton, leadFormButton, purchaseButton, prequalifyButton, deliveryButton, contactSellerButton]
  );

  const renderLeadForm = leadForm => {
    const leadFormComponent = get(leadForm, 'leadForm.component');
    const leadFormData = get(leadForm, 'leadForm.data');
    const isNewVin = isNew(vinData.type);
    const dataTrackingLeadForm = getLeadFormCreativeId({
      isNewVin,
      pageNameLegacy,
      formName: LEAD_FORMS_CREATIVE_ID.PANEL_EMAIL_FORM,
    });

    return (
      <LeadFormDrawer
        url=""
        isOpen={isOpen}
        onDrawerToggle={toggleLeadFormDrawer}
        drawerContentWrapperClassName="px-1 px-md-2_5"
        drawerContentClassName="bg-cool-gray-90"
        closeBtnComponent={WhiteBackButton}
        drawerComponent={DrawerV2}
        shouldRemoveOnClose={false}
        isLFComponentContainerFluid
        containerClassName="pos-st p-0"
        component={leadFormComponent}
        componentData={{
          ...leadFormData,
          options: {
            ...leadFormData.options,
            toggleDrawer: toggleLeadFormDrawer,
          },
          params: {
            ...leadFormData.params,
            formName: LEAD_FORMS_CREATIVE_ID.PANEL_EMAIL_FORM,
            pageName: pageNameLegacy,
          },
        }}
        dataTrackingParent={dataTrackingLeadForm}
      />
    );
  };

  const ctas = useMemo(() => {
    const vdpCtas = getVdpCtas({
      vinData,
      isSimulatedLeadForm: ctasOptions?.isSimulatedLeadForm,
      location,
    }).filter(cta => LEAD_FORM_CTAS.includes(cta));

    return mapLeadFormCTAsToConfig({
      inventory: vinData,
      options: ctasOptions,
      national: false,
      ctas: vdpCtas,
      pageName,
      zipCode,
    });
  }, [ctasOptions, location, pageName, vinData, zipCode]);

  const buttons = useMemo(
    () =>
      getButtonsConfig({
        vinData,
        hideLeadForm,
        leadFormCtas: ctas,
        isCaramelOptInDealer,
        ignoreBolTreatment: isVlaPage,
        privateListingData,
        dealer,
        ted7485Recipe,
        enableCaramel,
        isShop5146NciCopyChallenge: ctasOptions?.isShop5146NciCopyChallenge,
      }),
    [
      ctas,
      hideLeadForm,
      isCaramelOptInDealer,
      isVlaPage,
      privateListingData,
      vinData,
      dealer,
      ted7485Recipe,
      enableCaramel,
      ctasOptions?.isShop5146NciCopyChallenge,
    ]
  );

  const dataTrackingParent = useMemo(() => {
    return getDataTrackingParent({
      vinData,
      ted6504Recipe,
      showCapOneButton: buttons.find(button => button.type === BUTTON_TYPES.PREQUALIFY) && isCapOneAvailable,
      isCapOneIneligible,
    });
  }, [vinData, ted6504Recipe, buttons, isCapOneAvailable, isCapOneIneligible]);

  if (!buttons.length) return null;

  return (
    <>
      <div className="sticky-bar w-100" data-tracking-parent={dataTrackingParent}>
        <div className="text-nowrap p-0_75 bg-white">
          <div
            className={classNames(
              'd-flex justify-content-center column-gap-0_5',
              isTed7485Recipe ? 'align-items-start' : 'align-items-center'
            )}
          >
            <ButtonsRenderer
              isTed7485Recipe={isTed7485Recipe}
              componentMap={COMPONENT_MAP}
              buttons={buttons}
              isPrivatePartyListing={isPrivatePartyListing}
            />
          </div>
        </div>
      </div>
      {!!ctas?.length && renderLeadForm(ctas[0])}
    </>
  );
};

StickyBarUI.propTypes = {
  vinData: InventoryEntities.InventoryVin.isRequired,
  location: PropTypes.shape({}),
  hideLeadForm: PropTypes.bool,
  ctasOptions: PropTypes.shape({}),
  isVlaPage: PropTypes.bool,
  isProdEnv: PropTypes.bool,
  dealerInfo: DealerInfoEntities.DealerInfo,
  isCapOneAvailable: PropTypes.bool,
  isCapOneIneligible: PropTypes.bool,
  handleCaramelMakeOffer: PropTypes.func,
  isCaramelOptInDealer: PropTypes.bool,
  ted6504Recipe: PropTypes.string,
  privateListingData: PropTypes.shape({}),
  dealer: PropTypes.shape({}),
  ted7485Recipe: PropTypes.string,
};

export const mapStateToProps = state => ({
  ted7485Recipe: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'TED-7485-FSBO-Messaging',
    defaultVal: 'ctrl',
  }),
});

export const stateToPropsConfig = {
  location: bindToPath('location', PageModel),
};

export const StickyBar = connect(mapStateToProps)(connectToModel(StickyBarUI, stateToPropsConfig));
